
import { defineComponent, computed, PropType, getCurrentInstance } from "vue";
import { getModule } from "vuex-module-decorators";
import { Constants } from "@app/constants";
import CiButton from "@components/base/button.vue";
import CiDropdownItem from "@components/base/dropdown-item.vue";
import CiDropdownMenu from "@components/base/dropdown-menu.vue";
import CiCheckbox from "@components/base/forms/checkbox.vue";
import CiRatingIcons from "@components/base/rating-icons.vue";
import CiRatingSri from "@components/base/rating-sri.vue";
import CiSfdr from "@components/base/sfdr.vue";
import CiPrice from "@components/products/price.vue";
import CiPriceUpdateBox from "@components/products/price-update-box.vue";
import CiRouterLink from "@router/router-link.vue";
import { utilityService } from "@services/utility.service";
import { ProductsStoreModule } from "@stores/products.store";
import { Emitter } from "@utils/events";
import { useI18n } from "@composables/i18n";

const productsStore = getModule(ProductsStoreModule);

export default defineComponent({
  name: "CiProductCollectionTable",
  components: {
    CiButton,
    CiCheckbox,
    CiDropdownItem,
    CiDropdownMenu,
    CiPrice,
    CiPriceUpdateBox,
    CiRatingIcons,
    CiRatingSri,
    CiRouterLink,
    CiSfdr,
    CiProductMediathekLabel: () => import("@components/products/mediathek-label.vue"),
  },
  props: {
    id: { type: String, default: "" },
    items: { type: Array as PropType<Products.TableData[]>, default: () => [] },
    action: { type: String as PropType<"toggle" | "remove">, default: "toggle" },
    rankingLink: { type: Boolean, default: true },
    priceUpdateBox: { type: Boolean, default: true },
    staticFields: { type: Array as PropType<string[][]>, default: () => [] },
    showDefaultStaticFields: { type: Boolean, default: true },
    highlightedProductId: { type: Number, default: 0 },
    simple: { type: Boolean, default: false },
    productClickHandler: { type: Function as PropType<(p: Products.TableData) => void>, default: () => () => ({}) },
    rankingClickHandler: { type: Function as PropType<(p: Products.TableData) => void>, default: () => () => ({}) },
  },
  setup(props) {
    const { t } = useI18n(getCurrentInstance());

    const defaultStaticFields = computed(() => {
      if (props.showDefaultStaticFields) {
        return [
          ["performance", t("ui.shared.global.metrics.performance.abbr_with_year_period", { period: 1 }) as string],
          ["volatility", t("ui.shared.products.table.heads.volatility") as string],
          ["price", t("ui.shared.products.table.heads.price") as string],
          ["max_drawdown", t("ui.shared.products.table.heads.max_draw_down") as string],
          ["sri", t("ui.shared.products.table.heads.sri") as string],
        ];
      }
      return [];
    });

    const fields = computed(() => {
      const staticFields = props.simple ? [] : [...defaultStaticFields.value, ...props.staticFields];
      return [
        { key: "name", label: "", stickyColumn: true },
        ...staticFields.map((field) => ({ key: field[0], label: field[1], class: "align-middle" })),
      ];
    });

    const comparisonProducts = computed(() => productsStore.comparisonProducts);

    const toggleDisabled = (item: Products.TableData) =>
      comparisonProducts.value.length >= Constants.comparisonListMaxSize &&
      !comparisonProducts.value.some((product) => product.id === item.id);

    const formatPercentage = (value: number) => utilityService.formatPercentage(value, " ");

    const handleComparisonToggle = (product: Products.TableData, remove = false) => {
      const isComparisonListFull = comparisonProducts.value.length >= Constants.comparisonListMaxSize;
      const isNewProduct = !comparisonProducts.value.some((p) => p.id === product.id);

      if (isComparisonListFull && isNewProduct) return;

      const comparisonProduct = comparisonProducts.value.find((p) => p.isin === product.isin);

      if (comparisonProduct || remove) {
        productsStore.removeProductFromComparison(product);
      } else {
        productsStore.addProductToComparison(product);
      }
    };

    const productOnComparisonList = (product: Products.TableData) =>
      comparisonProducts.value.some((p) => p.id === product.id);

    const highlightProduct = (item: Products.TableData) =>
      item.id === props.highlightedProductId ? "highlighted" : "";

    const rowHovered = (_event: MouseEvent, item: Products.TableData) => {
      Emitter.emit("ci::product-collection-item::hovered", item.id);
    };

    const rowLeft = (_event: MouseEvent, item: Products.TableData) => {
      Emitter.emit("ci::product-collection-item::left", item.id);
    };

    return {
      fields,
      defaultStaticFields,
      comparisonProducts,
      toggleDisabled,
      formatPercentage,
      handleComparisonToggle,
      productOnComparisonList,
      highlightProduct,
      rowHovered,
      rowLeft,
    };
  },
});
